// Backend transport for the marketing site, mirroring how the portal/admin
// apps isolate network calls in one module (see apps/portal/src/api.ts and
// @quintara/web-kit's HTTP client) rather than fetch()ing inside components.
// All of the "how we talk to the backend" — URL, timeout, JSON, response
// shape — lives here; pages just call submitContact(...). The actual logic
// (validation, captcha verification, rate limiting, email) lives in the
// quintara-contact backend, not here.
import { CAREERS_API_URL, CONTACT_API_URL } from "./constants";

/** Exactly the keys the quintara-contact backend's ALLOWED_KEYS accepts. */
export interface ContactPayload {
  name: string;
  email: string;
  company: string;
  phone: string;
  country: string;
  segment: string;
  volume: string;
  message: string;
  consent: string;
  website: string; // honeypot
  elapsed_ms: number;
  turnstile_token: string;
}

/** Normalised result. On failure, `error` is the backend's error code (e.g.
 * "validation", "too_fast", "rate_limited", "captcha_invalid"), or a transport
 * code ("timeout" | "network") when the request never completed. */
export type ContactResult =
  | { ok: true }
  | { ok: false; error: string; fields?: Record<string, string> };

const REQUEST_TIMEOUT_MS = 20_000;

export async function submitContact(payload: ContactPayload): Promise<ContactResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(CONTACT_API_URL, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    let body: { ok?: boolean; error?: string; fields?: Record<string, string> } | null = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    if (res.ok && body && body.ok === true) return { ok: true };
    return {
      ok: false,
      error: body && typeof body.error === "string" ? body.error : "send_failed",
      fields: body?.fields,
    };
  } catch (err) {
    return { ok: false, error: err instanceof DOMException && err.name === "AbortError" ? "timeout" : "network" };
  } finally {
    clearTimeout(timer);
  }
}

// Careers application — multipart (carries the CV file), POSTed to the same
// backend at /careers. Uses XHR instead of fetch so we can report upload
// progress for the CV. Same normalised result shape as submitContact.
export function submitCareers(fd: FormData, onProgress?: (pct: number) => void): Promise<ContactResult> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", CAREERS_API_URL);
    xhr.timeout = 120_000;
    xhr.setRequestHeader("Accept", "application/json");
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
      };
    }
    xhr.onload = () => {
      let body: { ok?: boolean; error?: string; fields?: Record<string, string> } | null = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        body = null;
      }
      if (xhr.status === 200 && body && body.ok === true) return resolve({ ok: true });
      resolve({
        ok: false,
        error: body && typeof body.error === "string" ? body.error : xhr.status === 413 ? "too_large" : "send_failed",
        fields: body?.fields,
      });
    };
    xhr.onerror = () => resolve({ ok: false, error: "network" });
    xhr.ontimeout = () => resolve({ ok: false, error: "timeout" });
    xhr.send(fd);
  });
}
